import { MissionControlOrderData, MissionControlOrderDataBlob, MissionControlOrderItem } from "../../common/common.interfaces"
import { ProductQueryResult } from "../../common/interfaces/product.order.interface"
import { RawRow, UniversalConstructorContainer, UniversalConstructorMaterials, UniversalConstructorPayload } from "../universalConstructor.interfaces"
import { apiFetch } from "../../utility/fetch"
import { excelDateToISOString } from "../../common/common.helpers"
import modifyEpicOrders from "./epic.mapper"
import modifyWootOrders from "./woot.mapper"

const SYSTEM_PARENT_CODE = 'SYSTEM' // use a special code to avoid needing separate column for special cases, like the sku lookup column
const SKU_RECORD_KEY = 'skuCode' //  So it will look like SYSTEM.skuCode and be mapped to e.g. ItemCode (for Epic)
const SPECIAL = 'SPECIAL' // might need to do something with this so just exclude it... you could also just not map it, but if necessary to hardcode you can use this
const REQUIRED_FIELDS = [
    'order.Name',
    'orderItem.UnitPrice',
    'orderItem.Quantity'
]
const REQUIRED_MSG = " is required to create an Order\n"


export default function buildOrders(params: UniversalConstructorMaterials) {

  const fieldMap = new Map<string, string>
  const SPECIAL_FIELD_HANDLERS: Record<string, (value: any) => any> = {
  'EffectiveDate': evaluateDate,
  'ShippingCountry': validateCountry
}

  /**
     * Process a list of order items common to an order and return a payload object
     * 
     * Here's a sample of what this will look like:
     * Input: Column: "customerPO" Row: "Test584820"
     * MapperConfigurationDataTableRow: missionControlField: "order.Name", mappedField: "customerPO"
     *
     * In code this will look like:
     * rawRow[customerPO]]: "Test584820"
     * mappedMissionControlKey: "order.Name", mappedRawKey: "customerPO"
     * fieldParent: "order", field: "Name"; rawValue: "Test584820"
     * @param rawOrderItemRows 
     * @param productDictionary 
     * @returns 
     */
  async function createOrders() {

    const listingCode = params.mapperConfigSet.listingCode
    // get mapper config
    for(const mappedFieldRow of params.mapperConfigSet.relations) {
      fieldMap.set(mappedFieldRow.missionControlField, mappedFieldRow.mappedField)
    }
    // map each order item to a set of order names
    const rowMap = new Map<string, RawRow[]>
    const uniqueSkus = []
    for (const row of params.rawRows) {
      const orderNum = getMCOrderNumEquivalent(row)
      if (!orderNum) {
        throw new Error(`Couldn't map Order Number. Did you select the correct Account?`)
      }
      if (!rowMap.has(orderNum)) {
        rowMap.set(orderNum, [row])
      } else {
        rowMap.get(orderNum).push(row)
      }
      // Need this later for the product lookup
      const skuKey = row[fieldMap.get([SYSTEM_PARENT_CODE, SKU_RECORD_KEY].join('.'))]
      if (!uniqueSkus.includes(skuKey)) {
        uniqueSkus.push(skuKey)
      }
    }

    // product lookup
    const productNamesIn = `'${Object.values(uniqueSkus).join(`','`)}'`
    
    const query = `SELECT Id, (SELECT Id, Name, SKU__c FROM Listings__r WHERE Name LIKE '%${listingCode}' AND SKU__c IN (${productNamesIn})), (SELECT Id FROM PricebookEntries WHERE Pricebook2Id = '01s1N0000066ylwQAA') FROM Product2 WHERE Id IN (SELECT Product__c FROM Listing__c WHERE Name LIKE '%${listingCode}' AND SKU__c IN (${productNamesIn}))`
    console.log(query)
    const productResponse = await apiFetch<{records: ProductQueryResult[]}>({
      endpoint: 'salesforceQuery',
      method: 'POST',
      accessToken: params.userData.accessToken,
      body: JSON.stringify({
        soql: query
      })
    })
    if(!productResponse.ok){
      throw new Error(`Failed to get Pricebook Entries from Mission Control.`)
    }

    // process raw order lines
    return mapOrders(rowMap, productResponse.body.records)

  }

  function mapOrders(rowMap: Map<string, RawRow[]>, productDictionary: ProductQueryResult[]) {

    const newOrders: UniversalConstructorPayload[] = []
    const failedOrders: UniversalConstructorPayload[] = []
    rowMap.forEach((rawOrderItemRows, orderNum) => {
      try {
        newOrders.push(mapOrderItems(rawOrderItemRows, productDictionary))
      } catch(error){
        console.warn({error})
        const errOrder: UniversalConstructorPayload = {
          missionControlOrder: undefined,
          original: rawOrderItemRows,
          error: error.toString()
        }
        failedOrders.push(errOrder)
      }
    })
    const container: UniversalConstructorContainer = {
      ordersSuccessful: newOrders,
      ordersFailed: failedOrders
    }

    /**
     * Integration specific mapping
     */
    // Epic specific
    if(params.mapperConfigSet.accountId === '0013l00002OBc1fAAD') {
      modifyEpicOrders(container, params)
    }
    // Woot specific
    if(params.mapperConfigSet.accountId === '0016O00003XcljaQAB') {
      modifyWootOrders(container, params)
    }

    return container

  }

  function mapOrderItems(rawOrderItemRows: RawRow[], productDictionary: ProductQueryResult[]) {

    let errMsg = ""

    const order: MissionControlOrderData = {
      AccountId: params.mapperConfigSet.accountId,
      Order_Integration__c: undefined,
      Status: 'Draft',
      Name: undefined,
      Order_Time__c: Date.now().toString(),
      EffectiveDate: undefined,
      Estimated_Ship_Date__c: undefined,
      Customer_Name__c: undefined,
      Customer_Phone_Number__c: undefined,
      Shipping_Address_Name__c: undefined,
      ShippingStreet: undefined,
      ShippingCity: undefined,
      ShippingState: undefined,
      ShippingPostalCode: undefined,
      ShippingCountry: undefined,
      Shipping_Collected__c: 0,
      Tax_Collected__c: 0,
      Order_Discount_Amount__c: 0,
      Shipping_Carrier_Requested__c: undefined,
      Shipping_Class_Requested__c: undefined,
      Pricebook2Id: '01s1N0000066ylwQAA',
      Unique_External_ID__c: undefined
    }
    const orderContainer: MissionControlOrderDataBlob = {
      order: order,
      orderItems: [],
      contact: undefined,
    }
    for (const rawRow of rawOrderItemRows) {
      const orderItem: MissionControlOrderItem = {
        UnitPrice: 0,
        Quantity: 0,
        PricebookEntryId: undefined,
        Product2Id: undefined,
        Line_Item_Title__c: undefined,
        Listing__c: undefined
      }

      for (const field of REQUIRED_FIELDS) {
        if (!rawRow[fieldMap.get(field)]) {
          errMsg = errMsg.concat(`${field}`).concat(REQUIRED_MSG)
          console.warn(errMsg.concat(`${JSON.stringify(rawRow)}`))
        }
      }

      // Lookup the listing in the product dictionary, exception if not found
      const product = productDictionary.find(li => li.Listings__r?.records?.[0]?.SKU__c.toUpperCase() === getSkuKey(rawRow).toUpperCase())
      if (!product) {
        errMsg = errMsg.concat(`${getSkuKey(rawRow)} did not match any Listing's Product in lookup\n`)
      } else {
        orderItem.Product2Id = product.Id
        orderItem.Listing__c = product.Listings__r?.records?.[0]?.Id
        orderItem.PricebookEntryId = product.PricebookEntries?.records?.[0]?.Id
      }

      fieldMap.forEach((mappedRawKey, mappedMissionControlKey) => {
        const [fieldParent, field] = mappedMissionControlKey.split('.')
        const rawValue = rawRow[mappedRawKey]
        // anything using SYSTEM_PARENT_CODE gets special handling outside this, eg product
        if (fieldParent !== SYSTEM_PARENT_CODE && mappedRawKey !== SPECIAL && rawValue !== undefined) {
          // Apply special field transformation if handler exists (stole this abstraction from chatgpt)
          const transformFn = SPECIAL_FIELD_HANDLERS[field]
          const parsedValue = transformFn ? transformFn(rawValue) : parseValue(rawValue.toString())
          if (fieldParent === 'orderItem') {
            orderItem[field] = parsedValue;
          } else {
            orderContainer[fieldParent][field] = parsedValue;
          }
        }
      })
      orderContainer.orderItems.push(orderItem)
    }
    const payload: UniversalConstructorPayload = {
      missionControlOrder: orderContainer,
      original: rawOrderItemRows
    }
    
    if (errMsg) {
      throw new Error(errMsg)
    }

    return payload

    
  }

  // #region HELPERS
  /**
   * parse a string to a bool if possible- stolen from chatgpt
   * @param value
   * @returns 
   */
  function parseValue(value: string): string | number | boolean {
    if (value.toLowerCase() === 'true') return true
    if (value.toLowerCase() === 'false') return false
    return value
  }

  /**
   * Stole from gpt
   * @param excelDate 
   * @returns 
   */
  function evaluateDate(excelDate) {
    const conversionDate = excelDateToISOString(excelDate)
    if (!conversionDate) {
      return excelDate
    } else {
      return conversionDate
    }
  }

  /**
   * Check a country string against what we actually accept as input for orders
   * @param country 
   * @returns 
   */
  function validateCountry(country: string) {
      const match = params.countryCodesSet.find(entry => entry.PartitionKey === country)

      if(match?.RowKey) {
        return match.RowKey
      } else { // fallback if not found
        return country.slice(0, 2)
      }
  }

  /**
   * Gets whatever is mapped to the Name field that will be used for the internal Order Number
   */
  function getEquivalentField (row: RawRow, field: string) {
    return row[fieldMap.get(field)]
  }

  /**
   * Gets whatever is mapped to the Name field that will be used for the internal Order Number
   */
  function getMCOrderNumEquivalent (row: RawRow) {
    return getEquivalentField(row, 'order.Name')
  }

  /**
   * Gets whatever is mapped to the equivalent product name that will be used for listing lookup
   */
  function getSkuKey (row: RawRow) { 
    return getEquivalentField(row, [SYSTEM_PARENT_CODE, SKU_RECORD_KEY].join('.'))
  }

  
  return createOrders()

}

